



























































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { ClientUserRow } from "@/data/models/ClientAdministrationRow";
import { Licence, localiceLicence } from "@/data/models/Licence";
import { Role } from "@/data/models/Role";
import { ValidationLevel } from "@/data/models/User";
import { getClientUsersForAdministration } from "@/data/repository/ClientAdministrationRepository";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import {
  DEFAULT_USER_LIST_SELECTION,
  isDefaultUserListSelection,
  orderedUserListSelection,
  selectUserListRows,
  USER_LIST_OPTIONS,
  USER_LIST_SORTS,
  UserListOption,
  userListOptionGroup,
  UserListSort,
  userStatus,
  UserStatusOption,
} from "@/utils/ClientUserList";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatUserRole, formatUserValidationLevel } from "@/utils/LocalisationHelper";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";

/**
 * A client's users in the admin portal, read-only, with the fields IRES Admin and IRES Office's own
 * Benutzer list show: nickname, first and last name, role, validation level, licences and status.
 * Roles carry the names the client itself uses (its role settings, the master's for a member client).
 *
 * Opened from the client detail's Benutzer count. Deleted users are hidden by the default filter, as
 * IRES Admin hides them; ticking Gelöscht shows them (Taimoor, 2026-10-02).
 *
 * **Not the access boundary**: the data comes from `getClientUsersForAdministration`, which repeats
 * the portal and `MANAGE_CLIENTS` checks server-side and returns only these fields: no contact data,
 * address, staff number, password data or session token. Nothing here writes: IRES Admin's password
 * reset and web-admin creation are not part of this screen.
 */
@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientAdministrationUsersComponent extends Vue {
  users: ClientUserRow[] = [];
  roleLabels: Record<string, string> = {};
  isLoading = true;
  loadError = false;
  searchQuery = "";
  selection: UserListOption[] = [...DEFAULT_USER_LIST_SELECTION];
  sort: UserListSort = UserListSort.NICKNAME;

  readonly emptyValue = EMPTY_VALUE;

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get rows(): ClientUserRow[] {
    return selectUserListRows(this.users, this.selection, this.searchQuery, this.sort);
  }

  get filterOptions(): UserListOption[] {
    return USER_LIST_OPTIONS.map((entry) => entry.option);
  }

  get defaultSelection(): UserListOption[] {
    return DEFAULT_USER_LIST_SELECTION;
  }

  get sortOptions(): UserListSort[] {
    return USER_LIST_SORTS;
  }

  /** The ticked options in menu order; nothing for the default (every user who is not deleted). */
  get filterDescription(): string {
    if (isDefaultUserListSelection(this.selection)) {
      return "";
    }

    return orderedUserListSelection(this.selection).map((option) => this.optionName(option)).join(", ");
  }

  /** IRES Office's own Benutzer list wording. */
  sortName(sort: UserListSort): string {
    switch (sort) {
      case UserListSort.FIRST_NAME:
        return this.$t("users.firstName").toString();
      case UserListSort.LAST_NAME:
        return this.$t("users.lastName").toString();
      default:
        return this.$t("users.nickname").toString();
    }
  }

  optionKey(option: UserListOption): string {
    return option;
  }

  optionGroupName(option: UserListOption): string {
    return this.$t(`admin.clientUsers.filterGroups.${userListOptionGroup(option)}`).toString();
  }

  optionName(option: UserListOption): string {
    switch (option) {
      case UserStatusOption.ACTIVE:
      case UserStatusOption.BLOCKED:
      case UserStatusOption.DELETED:
        return this.$t(`admin.clientUsers.statuses.${option}`).toString();
      default:
        return this.roleLabel(option);
    }
  }

  /** The client's own name for the role, else the standard one, as `getRoleLabelForRoleSettings` does. */
  roleLabel(role: string | null): string {
    if (!role) {
      return EMPTY_VALUE;
    }

    return this.roleLabels[role] ?? (formatUserRole(this, role as Role) || role);
  }

  validationLevelLabel(user: ClientUserRow): string {
    return formatUserValidationLevel(this, (user.validationLevel ?? undefined) as ValidationLevel | undefined) || EMPTY_VALUE;
  }

  /** Each licence by IRES Office's own name; a licence this build does not know is shown as stored. */
  licenceNames(user: ClientUserRow): string[] {
    return user.licences.map((licence) => localiceLicence(licence as Licence, this) ?? licence);
  }

  /**
   * The first letter of each licence name, as IRES Office's Benutzer list shows them (`UserItem`'s
   * `licences`): "BAT" for Basis, Aufnahme, Trocknung in German.
   */
  licencesLabel(user: ClientUserRow): string {
    const names = this.licenceNames(user);
    return names.length === 0 ? EMPTY_VALUE : names.map((name) => name.substring(0, 1)).join("");
  }

  statusLabel(user: ClientUserRow): string {
    return this.$t(`admin.clientUsers.statuses.${userStatus(user)}`).toString();
  }

  onBack() {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: this.clientId } });
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;

    try {
      const list = this.clientId === ""
        ? { users: [], roleLabels: {} }
        : await getClientUsersForAdministration(this.clientId);
      this.users = list.users;
      this.roleLabels = list.roleLabels;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud
      // included), and the refresh button is the only useful action.
      this.loadError = true;
      this.users = [];
      this.roleLabels = {};
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientUsers.title").toString());
    this.load();
  }
}
